import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  MessageCircle,
  CalendarDays,
  MapPin,
  Box,
  CheckCircle,
  XCircle,
  Clock,
  Package,
  User,
  Edit,
  Trash2,
  MoreVertical,
} from 'lucide-react';
import airportImage from '../../assets/airportt.png';

const PAGE_SIZE = 12;

const formatDate = date => {
  if (!date) return 'N/A';
  let dateObj;
  if (typeof date === 'number') {
    dateObj = new Date(date);
  } else if (date && typeof date.toDate === 'function') {
    dateObj = date.toDate();
  } else if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'string') {
    dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
      return 'N/A';
    }
  } else {
    return 'N/A';
  }

  if (isNaN(dateObj.getTime())) {
    return 'N/A';
  }

  return dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatTime = time => {
  if (!time) return 'N/A';
  if (typeof time === 'string') {
    return time;
  }
  if (time instanceof Date) {
    return time.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }
  return 'N/A';
};

const getStatusIcon = status => {
  switch (status) {
    case 'pending':
      return <Clock className="h-4 w-4 text-yellow-500" />;
    case 'accepted':
      return <CheckCircle className="h-4 w-4 text-blue-500" />;
    case 'rejected':
      return <XCircle className="h-4 w-4 text-red-500" />;
    case 'completed':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'delivered':
      return <Package className="h-4 w-4 text-purple-500" />;
    default:
      return <Clock className="h-4 w-4 text-gray-500" />;
  }
};

const getStatusColor = status => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-50 border-yellow-200 text-yellow-800';
    case 'accepted':
      return 'bg-blue-50 border-blue-200 text-blue-800';
    case 'rejected':
      return 'bg-red-50 border-red-200 text-red-800';
    case 'completed':
      return 'bg-green-50 border-green-200 text-green-800';
    case 'delivered':
      return 'bg-purple-50 border-purple-200 text-purple-800';
    default:
      return 'bg-gray-50 border-gray-200 text-gray-800';
  }
};

const getStatusLabel = status => {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'accepted':
      return 'Accepted';
    case 'rejected':
      return 'Rejected';
    case 'completed':
      return 'Completed';
    case 'delivered':
      return 'Delivered';
    default:
      return status?.toUpperCase() || 'Unknown';
  }
};

export default function IncomingRequests() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, initializing } = usePublicAuth();
  const [trips, setTrips] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingRequest, setProcessingRequest] = useState(null);
  const [deletingTrip, setDeletingTrip] = useState(null);
  const [openMenuTripId, setOpenMenuTripId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const isLoading = initializing || loading;

  const carryingTrips = trips.filter(trip => trip.tripType === 'carrying');

  // Sort trips by creation date (newest first) before pagination
  const sortedTrips = useMemo(() => {
    return [...carryingTrips].sort((a, b) => {
      const aTime = a.createdAt?.toDate
        ? a.createdAt.toDate().getTime()
        : a.createdAt
        ? new Date(a.createdAt).getTime()
        : 0;
      const bTime = b.createdAt?.toDate
        ? b.createdAt.toDate().getTime()
        : b.createdAt
        ? new Date(b.createdAt).getTime()
        : 0;
      return bTime - aTime; // Newest first
    });
  }, [carryingTrips]);

  // Pagination logic
  const totalPages = Math.max(1, Math.ceil(sortedTrips.length / PAGE_SIZE));
  const paginatedTrips = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    return sortedTrips.slice(startIndex, endIndex);
  }, [sortedTrips, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(Math.max(1, totalPages));
    }
    // Reset to page 1 when new trips are added
    if (sortedTrips.length > 0 && currentPage === 1) {
      // Force re-render to show newest trips
    }
  }, [sortedTrips.length, currentPage, totalPages]);

  useEffect(() => {
    if (initializing) {
      return;
    }

    if (!user?.uid) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const loadData = async () => {
      try {
        // Load user's carrying trips
        const tripsQuery = query(
          collection(db, 'trips'),
          where('createdBy', '==', user.uid),
          where('tripType', '==', 'carrying'),
        );

        const tripsSnapshot = await getDocs(tripsQuery);
        const tripsData = tripsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        setTrips(tripsData);

        // Load all requests for these trips (Firestore 'in' query limit is 10)
        if (tripsData.length > 0) {
          const tripIds = tripsData.map(t => t.id);
          const allRequests = [];

          // Process in batches of 10
          for (let i = 0; i < tripIds.length; i += 10) {
            const batch = tripIds.slice(i, i + 10);
            const requestsQuery = query(
              collection(db, 'requests'),
              where('tripId', 'in', batch),
            );

            const requestsSnapshot = await getDocs(requestsQuery);
            const batchRequests = requestsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
            }));

            allRequests.push(...batchRequests);
          }

          setRequests(allRequests);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Set up real-time listeners
    if (user?.uid) {
      const tripsQuery = query(
        collection(db, 'trips'),
        where('createdBy', '==', user.uid),
        where('tripType', '==', 'carrying'),
      );

      const unsubscribeTrips = onSnapshot(tripsQuery, snapshot => {
        const tripsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setTrips(tripsData);

        // Update requests when trips change (Firestore 'in' query limit is 10)
        if (tripsData.length > 0) {
          const tripIds = tripsData.map(t => t.id);
          const tripIdSet = new Set(tripIds);
          const unsubscribes = [];

          // Set up listeners for batches of 10
          for (let i = 0; i < tripIds.length; i += 10) {
            const batch = tripIds.slice(i, i + 10);
            const requestsQuery = query(
              collection(db, 'requests'),
              where('tripId', 'in', batch),
            );

            const unsubscribe = onSnapshot(requestsQuery, snapshot => {
              const requestsData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
              }));

              setRequests(prev => {
                // Filter out requests that don't belong to current trips
                const filtered = prev.filter(r => tripIdSet.has(r.tripId));
                // Add/update requests from this batch
                const existingIds = new Set(filtered.map(r => r.id));
                const newRequests = requestsData.filter(
                  r => !existingIds.has(r.id),
                );
                return [...filtered, ...newRequests];
              });
            });

            unsubscribes.push(unsubscribe);
          }

          return () => {
            unsubscribes.forEach(unsub => unsub());
          };
        } else {
          setRequests([]);
        }
      });

      return () => unsubscribeTrips();
    }
  }, [user?.uid, initializing]);

  const handleAcceptRequest = async requestId => {
    if (processingRequest) return;
    setProcessingRequest(requestId);
    try {
      const requestRef = doc(db, 'requests', requestId);
      await updateDoc(requestRef, {
        status: 'accepted',
        updatedAt: new Date(),
      });
      alert(t('incomingRequests.requestAcceptedSuccessfully'));
    } catch (error) {
      console.error('Error accepting request:', error);
      alert(t('incomingRequests.failedToAcceptRequest'));
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleRejectRequest = async requestId => {
    if (processingRequest) return;
    if (!confirm(t('incomingRequests.areYouSureReject'))) {
      return;
    }
    setProcessingRequest(requestId);
    try {
      const requestRef = doc(db, 'requests', requestId);
      await updateDoc(requestRef, {
        status: 'rejected',
        updatedAt: new Date(),
      });
      alert(t('incomingRequests.requestRejected'));
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert(t('incomingRequests.failedToRejectRequest'));
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleEditTrip = trip => {
    navigate('/request-delivery', {
      state: {
        editMode: true,
        tripData: trip,
      },
    });
  };

  const handleDeleteTrip = async tripId => {
    if (deletingTrip) return;
    if (
      !confirm(
        t('incomingRequests.areYouSureDeleteTrip'),
      )
    ) {
      return;
    }
    setDeletingTrip(tripId);
    try {
      const tripRef = doc(db, 'trips', tripId);
      await deleteDoc(tripRef);
      alert(t('incomingRequests.tripDeletedSuccessfully'));
    } catch (error) {
      console.error('Error deleting trip:', error);
      alert(t('incomingRequests.failedToDeleteTrip'));
    } finally {
      setDeletingTrip(null);
    }
  };

  const getRequestsForTrip = tripId => {
    return requests.filter(req => req.tripId === tripId);
  };

  const renderRequestCard = (request, trip) => {
    const canAccept = request.status === 'pending';
    const canReject =
      request.status === 'pending' || request.status === 'accepted';

    return (
      <div className="rounded-2xl bg-white dark:bg-slate-900 p-6 shadow-lg border-2 border-gray-200 dark:border-slate-800 mb-4">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <User className="h-5 w-5 text-gray-500 dark:text-slate-400" />
              <span className="font-bold text-gray-900 dark:text-white">
                {request.requesterName ||
                  request.createdByName ||
                  'Unknown User'}
              </span>
            </div>
            {request.requesterEmail && (
              <p className="text-sm text-gray-600 dark:text-slate-400 mb-2">
                {request.requesterEmail}
              </p>
            )}
          </div>
          <div
            className={`flex items-center gap-2 px-3 py-1 rounded-full border-2 ${getStatusColor(
              request.status,
            )}`}
          >
            {getStatusIcon(request.status)}
            <span className="text-xs font-bold">
              {getStatusLabel(request.status)}
            </span>
          </div>
        </div>

        <div className="space-y-3 mb-4">
          {request.itemDescription && (
            <div className="flex items-start gap-2">
              <Package className="h-4 w-4 text-gray-500 dark:text-slate-400 mt-0.5" />
              <div>
                <p className="text-xs font-bold uppercase text-gray-500 dark:text-slate-400 mb-1">
                  Item Description
                </p>
                <p className="text-sm text-gray-900 dark:text-white">
                  {request.itemDescription}
                </p>
              </div>
            </div>
          )}

          {request.availableSpace && (
            <div className="flex items-center gap-2">
              <Box className="h-4 w-4 text-gray-500 dark:text-slate-400" />
              <div>
                <p className="text-xs font-bold uppercase text-gray-500 dark:text-slate-400 mb-1">
                  {t('incomingRequests.spaceNeeded')}
                </p>
                <p className="text-sm text-gray-900 dark:text-white">
                  {request.availableSpace} {request.spaceUnit || 'kg'}
                </p>
              </div>
            </div>
          )}

          {request.notes && (
            <div className="p-3 bg-gray-50 dark:bg-slate-800 rounded-lg">
                <p className="text-xs font-bold uppercase text-gray-500 dark:text-slate-400 mb-1">
                  {t('incomingRequests.notes')}
                </p>
              <p className="text-sm text-gray-700 dark:text-slate-300">
                {request.notes}
              </p>
            </div>
          )}
        </div>

        {(canAccept || canReject) && (
          <div className="flex gap-3 pt-4 border-t-2 border-gray-100 dark:border-slate-700">
            {canAccept && (
              <button
                onClick={() => handleAcceptRequest(request.id)}
                disabled={processingRequest === request.id}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition disabled:opacity-50"
              >
                <CheckCircle className="h-5 w-5" />
                {t('incomingRequests.accept')}
              </button>
            )}
            {canReject && (
              <button
                onClick={() => handleRejectRequest(request.id)}
                disabled={processingRequest === request.id}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition disabled:opacity-50"
              >
                <XCircle className="h-5 w-5" />
                {t('incomingRequests.reject')}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderTripCard = trip => {
    const tripRequests = getRequestsForTrip(trip.id);
    const pendingCount = tripRequests.filter(
      r => r.status === 'pending',
    ).length;
    const acceptedCount = tripRequests.filter(
      r => r.status === 'accepted',
    ).length;
    const completedCount = tripRequests.filter(
      r => r.status === 'completed' || r.status === 'delivered',
    ).length;

    // Check if trip is completed (all requests are completed/delivered or trip status is completed)
    const isCompleted =
      trip.status === 'completed' ||
      (tripRequests.length > 0 &&
        tripRequests.every(
          r =>
            r.status === 'completed' ||
            r.status === 'delivered' ||
            r.status === 'rejected',
        ));

    return (
      <div
        className={`group relative rounded-2xl p-6 shadow-md border-2 transition-all duration-300 ${
          isCompleted
            ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 hover:border-green-400 dark:hover:border-green-600'
            : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800 hover:shadow-xl hover:border-gray-300 dark:hover:border-slate-700'
        }`}
      >
        {/* Completed Badge */}
        {isCompleted && (
          <div className="mb-4 flex items-center justify-center">
            <div className="flex items-center gap-2 px-4 py-2 bg-green-600 dark:bg-green-500 text-white dark:text-black rounded-full">
              <CheckCircle className="h-5 w-5" />
              <span className="text-sm font-bold">{t('incomingRequests.completed')}</span>
            </div>
          </div>
        )}

        {/* Route Header */}
        <div className="mb-4 pb-4 border-b border-gray-100 dark:border-slate-700">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-1">
                  {t('incomingRequests.route')}
                </p>
                  <p className="text-base font-bold text-gray-900 dark:text-white leading-tight truncate">
                    {trip.originDetails?.city || trip.originCity || 'Origin'}
                    {trip.originDetails?.country || trip.originCountry
                      ? `, ${trip.originDetails?.country || trip.originCountry}`
                      : ''}{' '}
                    <span className="text-gray-400 dark:text-slate-500">→</span>{' '}
                    {trip.destinationDetails?.city ||
                      trip.destinationCity ||
                      'Destination'}
                    {trip.destinationDetails?.country || trip.destinationCountry
                      ? `, ${
                          trip.destinationDetails?.country ||
                          trip.destinationCountry
                        }`
                      : ''}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-slate-400">
            <div className="flex items-center gap-1.5">
              <CalendarDays className="h-4 w-4 text-blue-500 dark:text-blue-400" />
              <span className="font-medium">
                {formatDate(trip.departureDate)} at{' '}
                {formatTime(trip.departureTime)}
              </span>
            </div>
          </div>
        </div>

        {/* Item Description */}
        {trip.itemDescription && (
          <div className="mb-4">
            <div className="flex items-start gap-2">
              <Package className="h-4 w-4 text-purple-500 dark:text-purple-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold uppercase text-gray-500 dark:text-slate-400 mb-1">
                  {t('incomingRequests.itemDescription')}
                </p>
                <p className="text-sm text-gray-900 dark:text-white break-words line-clamp-2">
                  {trip.itemDescription}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="mb-4 p-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-slate-800 dark:to-slate-700 rounded-xl">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-yellow-500 dark:text-yellow-400 flex-shrink-0" />
              <span className="text-xs font-bold text-gray-900 dark:text-white">
                {pendingCount}
              </span>
              <span className="text-xs text-gray-600 dark:text-slate-400">
                {t('incomingRequests.pending')}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle className="h-4 w-4 text-blue-500 dark:text-blue-400 flex-shrink-0" />
              <span className="text-xs font-bold text-gray-900 dark:text-white">
                {acceptedCount}
              </span>
              <span className="text-xs text-gray-600 dark:text-slate-400">
                {t('incomingRequests.accepted')}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <MessageCircle className="h-4 w-4 text-gray-500 dark:text-slate-400 flex-shrink-0" />
              <span className="text-xs font-bold text-gray-900 dark:text-white">
                {tripRequests.length}
              </span>
              <span className="text-xs text-gray-600 dark:text-slate-400">
                {t('incomingRequests.total')}
              </span>
            </div>
            {completedCount > 0 && (
              <div className="flex items-center gap-1.5">
                <CheckCircle className="h-4 w-4 text-green-500 dark:text-green-400 flex-shrink-0" />
                <span className="text-xs font-bold text-gray-900 dark:text-white">
                  {completedCount}
                </span>
                <span className="text-xs text-gray-600 dark:text-slate-400">
                  {t('incomingRequests.done')}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              navigate(`/trip-requests/${trip.id}`);
            }}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-black dark:bg-yellow-500 text-white dark:text-black rounded-xl text-sm font-semibold hover:bg-gray-900 dark:hover:bg-yellow-400 transition-all duration-200"
          >
            <MessageCircle className="h-4 w-4" />
            {t('incomingRequests.view')} ({tripRequests.length})
          </button>
          {!isCompleted && (
            <div className="relative">
              <button
                onClick={() =>
                  setOpenMenuTripId(openMenuTripId === trip.id ? null : trip.id)
                }
                className="flex items-center justify-center p-2.5 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 rounded-xl hover:bg-gray-200 dark:hover:bg-slate-700 transition"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
              {openMenuTripId === trip.id && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setOpenMenuTripId(null)}
                  ></div>
                  <div className="absolute right-0 top-full mt-2 z-20 w-40 bg-white dark:bg-slate-900 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                    <button
                      onClick={() => {
                        handleEditTrip(trip);
                        setOpenMenuTripId(null);
                      }}
                      disabled={deletingTrip === trip.id}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition disabled:opacity-50"
                    >
                      <Edit className="h-4 w-4" />
                      {t('incomingRequests.edit')}
                    </button>
                    <button
                      onClick={() => {
                        handleDeleteTrip(trip.id);
                        setOpenMenuTripId(null);
                      }}
                      disabled={deletingTrip === trip.id}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition disabled:opacity-50"
                    >
                      {deletingTrip === trip.id ? (
                        <Clock className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                      {t('incomingRequests.delete')}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Show skeleton while loading or initializing
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />

        {/* Hero Section Skeleton */}
        <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[500px]">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="h-16 bg-white/20 dark:bg-slate-700/50 rounded-xl mb-4 w-3/4 animate-pulse"></div>
                <div className="h-6 bg-white/20 dark:bg-slate-700/50 rounded-lg mb-2 w-full animate-pulse"></div>
                <div className="h-6 bg-white/20 dark:bg-slate-700/50 rounded-lg mb-2 w-5/6 animate-pulse"></div>
                <div className="h-6 bg-white/20 dark:bg-slate-700/50 rounded-lg w-4/6 animate-pulse"></div>
              </div>
              <div className="relative">
                <div className="w-full h-96 bg-white/20 dark:bg-slate-700/50 rounded-2xl animate-pulse"></div>
              </div>
            </div>
          </div>
        </section>

        <main className="mx-auto max-w-6xl px-4 pt-16 pb-16 sm:px-6 lg:px-8">
          {/* Cards Skeleton */}
          <div className="mb-8">
            <div className="h-8 bg-gray-200 dark:bg-slate-800 rounded-lg w-64 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded-lg w-96 animate-pulse"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, index) => (
              <div
                key={index}
                className="rounded-2xl bg-white dark:bg-slate-900 p-6 shadow-md border border-gray-200 dark:border-slate-800"
              >
                <div className="h-6 bg-gray-200 dark:bg-slate-800 rounded-lg w-3/4 mb-4 animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded-lg w-full mb-2 animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded-lg w-5/6 mb-4 animate-pulse"></div>
                <div className="h-20 bg-gray-200 dark:bg-slate-800 rounded-xl mb-4 animate-pulse"></div>
                <div className="flex gap-3 mb-4">
                  <div className="h-8 bg-gray-200 dark:bg-slate-800 rounded-lg flex-1 animate-pulse"></div>
                  <div className="h-8 bg-gray-200 dark:bg-slate-800 rounded-lg flex-1 animate-pulse"></div>
                  <div className="h-8 bg-gray-200 dark:bg-slate-800 rounded-lg flex-1 animate-pulse"></div>
                </div>
                <div className="h-12 bg-gray-200 dark:bg-slate-800 rounded-xl animate-pulse"></div>
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <main className="mx-auto max-w-6xl px-4 pt-28 pb-16 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-8 shadow-lg text-center border border-gray-200 dark:border-slate-800">
            <p className="text-gray-600 dark:text-slate-400 mb-4">
              {t('incomingRequests.pleaseLogInToView')}
            </p>
            <a
              href="/auth/login"
              className="inline-flex rounded-2xl bg-black dark:bg-yellow-500 px-5 py-3 text-sm font-semibold text-yellow-400 dark:text-black transition hover:bg-gray-900 dark:hover:bg-yellow-400"
            >
              {t('incomingRequests.goToLogin')}
            </a>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[500px]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-black text-white mb-4 drop-shadow-lg">
                {t('incomingRequests.incomingRequests')}
              </h1>
              <p className="text-xl text-yellow-50 mb-8 max-w-2xl">
                {t('incomingRequests.manageRequestsFromTravelers')}
              </p>
            </div>
            <div className="relative">
              <img
                src={airportImage}
                alt="Airport"
                className="w-full h-auto object-contain"
                style={{ maxHeight: '400px' }}
              />
            </div>
          </div>
        </div>

        {/* Decorative bottom gradient */}
        <div
          className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-white/20 to-transparent"
          style={{ zIndex: 1 }}
        ></div>
      </section>

      <main className="mx-auto max-w-6xl px-4 pt-16 pb-16 sm:px-6 lg:px-8">
        {carryingTrips.length === 0 ? (
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-12 shadow-lg text-center border border-gray-200 dark:border-slate-800">
            <Box className="h-16 w-16 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t('incomingRequests.noDeliveryListingsYet')}
            </h2>
            <p className="text-gray-600 dark:text-slate-400 mb-6">
              {t('incomingRequests.createDeliveryRequest')}
            </p>
            <a
              href="/request-delivery"
              className="inline-flex rounded-2xl bg-black dark:bg-yellow-500 px-6 py-3 text-sm font-semibold text-yellow-400 dark:text-black transition hover:bg-gray-900 dark:hover:bg-yellow-400"
            >
              {t('incomingRequests.createRequest')}
            </a>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedTrips.map(trip => (
                <div key={trip.id}>{renderTripCard(trip)}</div>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 transition hover:border-gray-500 disabled:opacity-50 dark:border-slate-700 dark:text-slate-100 dark:hover:border-slate-500"
                >
                  {t('common.previous')}
                </button>
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => setCurrentPage(index + 1)}
                    className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                      currentPage === index + 1
                        ? 'bg-black text-yellow-400 dark:bg-yellow-500 dark:text-black'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  onClick={() =>
                    setCurrentPage(prev => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 transition hover:border-gray-500 disabled:opacity-50 dark:border-slate-700 dark:text-slate-100 dark:hover:border-slate-500"
                >
                  {t('common.next')}
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
